import { useCallback, useEffect, useState } from 'react';
import {
  fetchReleaseNotes,
  createReleaseNote,
  updateReleaseNote,
  deleteReleaseNote,
  publishReleaseNote,
  resendReleaseNote,
  type ReleaseNote,
  type ReleaseNotePayload,
} from '../services/releaseNotes.service';
import { describeApiError } from './errorMessage';

const PAGE_SIZE = 20;

/**
 * Admin-side list state for the Release Notes tab: paging, plus the mutations
 * that all end with a refetch so the row's status/send counts stay accurate.
 */
export function useReleaseNotes() {
  const [notes, setNotes] = useState<ReleaseNote[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetchReleaseNotes(page, PAGE_SIZE, statusFilter || undefined);
      setNotes(res.data ?? []);
      setTotal(res.pagination?.total ?? 0);
    } catch (err: any) {
      setError(describeApiError(err, 'Failed to load release notes'));
    } finally {
      setIsLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = useCallback(
    async (payload: ReleaseNotePayload) => {
      const res = await createReleaseNote(payload);
      await load();
      return res.data;
    },
    [load],
  );

  const update = useCallback(
    async (id: string, payload: Partial<ReleaseNotePayload>) => {
      const res = await updateReleaseNote(id, payload);
      await load();
      return res.data;
    },
    [load],
  );

  const remove = useCallback(
    async (id: string) => {
      await deleteReleaseNote(id);
      await load();
    },
    [load],
  );

  const publish = useCallback(
    async (id: string) => {
      const res = await publishReleaseNote(id);
      await load();
      return res.data;
    },
    [load],
  );

  const resend = useCallback(
    async (id: string, failedOnly = false) => {
      const res = await resendReleaseNote(id, failedOnly);
      await load();
      return res.data;
    },
    [load],
  );

  return {
    notes,
    total,
    page,
    setPage,
    statusFilter,
    setStatusFilter,
    isLoading,
    error,
    pageSize: PAGE_SIZE,
    reload: load,
    create,
    update,
    remove,
    publish,
    resend,
  };
}
